import { format, isToday, isTomorrow } from 'date-fns'
import { Box } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Badge, Text } from '@percent/lemonade'
import {
  ExternalParticipationStatus,
  OpportunitySelfParticipationDto
} from '@percent/workplace-giving/api/opportunity/dto'
import classes from './UpcomingItem.module.scss'
import { useDateTimeFmt } from '@percent/workplace-giving/common/hooks'

export function UpcomingItem({ opportunity, participationStatus }: OpportunitySelfParticipationDto) {
  const { t } = useTranslation()
  const { formatDurationDateTime } = useDateTimeFmt()

  const getDayName = (date: string) => {
    const formattedDate = new Date(date)

    if (isToday(formattedDate)) {
      return t('workplace_giving.upcoming.today')
    }

    if (isTomorrow(formattedDate)) {
      return t('workplace_giving.upcoming.tomorrow')
    }

    return format(formattedDate, 'EEEE')
  }

  const opportunityHref = `/volunteer/opportunities/${opportunity.id}`
  const dayLabel = opportunity.startDate
    ? `${getDayName(opportunity.startDate)}, ${format(new Date(opportunity.startDate), 'MMMM dd')}`
    : undefined

  return (
    <Box className={classes.Wrapper} data-testid="upcoming-item">
      <Box className={classes.Content}>
        <Box className={classes.TitleRow}>
          <Link className={classes.TitleLink} to={opportunityHref}>
            <Text size="medium" fontWeight="medium" className={classes.Title}>
              {opportunity.name}
            </Text>
          </Link>
          {participationStatus === ExternalParticipationStatus.Waitlisted && (
            <Badge variant="default" shape="oval">
              {t('workplace_giving.common.waitlisted')}
            </Badge>
          )}
        </Box>

        <Box className={classes.MetaRow}>
          {dayLabel ? (
            <Text className={classes.MetaItem} size="small" color="var(--colors-gray-600)">
              {dayLabel}
            </Text>
          ) : null}
          {opportunity.startDate ? (
            <Text className={classes.MetaItem} size="small" color="var(--colors-gray-600)">
              {formatDurationDateTime(
                new Date(opportunity.startDate),
                opportunity.endDate ? new Date(opportunity.endDate) : undefined
              )}
            </Text>
          ) : null}
          <Text className={classes.MetaItem} size="small" color="var(--colors-gray-600)">
            {t('workplace_giving.volunteering.upcoming.attendees', {
              count: opportunity.participantsCount
            })}
          </Text>
        </Box>
      </Box>
    </Box>
  )
}
